// src/views/NewJobLiteView.js
// ───────────────────────────────────────────────────────────────────────────
// 🟦 Intake de PRODUCTO (ESCRITORIO) — PLAN-INTAKE-PRODUCTOS Fase A
// Flujo del tipo "Producto": Cliente → Categoría → SKU del catálogo
// (o "+ nuevo producto") → cantidad → crea el job con skuRef (reorder-ready) y un
// snapshot inmutable de specs. El preview del trabajo es OBLIGATORIO (se ve en el
// Dashboard). Diagramación de escritorio (el sistema completo no es responsive;
// la versión "Lite" para CONSULTA desde teléfono es un módulo aparte, a futuro).
// Reusa: createJob, createProduct, ClientModal, ProductModal.
// ───────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, addDoc, getDoc, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Sparkles } from "lucide-react";
import ClientModal from "../components/ClientModal";
import ProductModal from "../components/ProductModal";
import { createJob } from "../lib/jobs";
import { createProduct } from "../lib/products";
import { resolveCategories } from "../lib/productCategories";

const PRIORITIES = ["Alta", "Media", "Baja"];

// Uploader de preview (escritorio): soporta clic (archivo), pegar y arrastrar.
const PreviewUploader = ({ cover, onSet }) => {
  const handleFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (ev) => onSet({ file, preview: ev.target.result });
    reader.readAsDataURL(file);
  };
  return (
    <div
      onPaste={(e) => { for (const it of e.clipboardData.items) if (it.type.startsWith("image/")) handleFile(it.getAsFile()); }}
      onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
      onDragOver={(e) => e.preventDefault()}
      className="relative flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg h-56 bg-gray-50 hover:bg-gray-100 cursor-pointer overflow-hidden"
    >
      {cover?.preview ? (
        <img src={cover.preview} alt="Preview" className="max-h-full max-w-full object-contain" />
      ) : (
        <div className="text-center text-gray-500 text-sm px-2">
          <p className="font-semibold">Pegar, arrastrar o hacer clic</p>
          <p className="text-xs">Foto/preview del trabajo (obligatorio)</p>
        </div>
      )}
      <input
        type="file"
        accept="image/*"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="absolute inset-0 opacity-0 cursor-pointer"
        title="Subir imagen"
      />
    </div>
  );
};

const NewJobLiteView = ({ db, user, storage }) => {
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [cover, setCover] = useState(null); // { file, preview }
  const [improving, setImproving] = useState(false);

  const handleImproveNotes = async () => {
    if (!form.description.trim() || improving) return;
    setImproving(true);
    try {
      const fn = httpsCallable(getFunctions(), "improveText");
      const res = await fn({ text: form.description, context: `notas del trabajo "${form.jobName || ""}"` });
      if (res.data?.improved) set("description", res.data.improved);
    } catch (e) {
      setError("La IA no pudo mejorar el texto: " + (e.message || e));
    } finally {
      setImproving(false);
    }
  };

  const [form, setForm] = useState({
    clientId: "",
    categoryId: "",
    productId: "",
    jobName: "",
    quantity: 1,
    priority: "Media",
    deliveryDate: "",
    description: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    setLoading(true);
    try {
      const [cliSnap, prodSnap, matSnap, cfgSnap] = await Promise.all([
        getDocs(collection(db, "clients")),
        getDocs(collection(db, "products")),
        getDocs(collection(db, "materials")),
        getDoc(doc(db, "project_settings", "productCategories")),
      ]);
      setClients(cliSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setProducts(prodSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setMaterials(matSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setCategories(resolveCategories(cfgSnap.exists() ? cfgSnap.data().categories : null));
    } catch (e) {
      setError("No se pudo cargar el catálogo.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [db]);

  const availableProducts = useMemo(() => {
    return products
      .filter((p) => p.active !== false)
      .filter((p) => !form.categoryId || p.categoryId === form.categoryId)
      .filter((p) => !form.clientId || !p.clientId || p.clientId === form.clientId)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [products, form.categoryId, form.clientId]);

  const selectedProduct = products.find((p) => p.id === form.productId) || null;

  useEffect(() => {
    if (selectedProduct && !form.jobName) set("jobName", selectedProduct.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.productId]);

  const handleSaveClient = async (dataToSave) => {
    try {
      const ref2 = await addDoc(collection(db, "clients"), { ...dataToSave, createdAt: serverTimestamp() });
      setClients((prev) => [...prev, { id: ref2.id, ...dataToSave }].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      set("clientId", ref2.id);
      setIsClientModalOpen(false);
    } catch (e) { setError("No se pudo crear el cliente: " + e.message); }
  };

  const handleSaveProduct = async (data) => {
    try {
      const { id } = await createProduct(db, { ...data, clientId: data.clientId || form.clientId || null }, user);
      await load();
      set("categoryId", data.categoryId);
      set("productId", id);
      if (!form.jobName) set("jobName", data.name);
      setIsProductModalOpen(false);
    } catch (e) { setError("No se pudo crear el producto: " + e.message); }
  };

  const canSave = form.clientId && form.productId && form.jobName.trim() && cover?.file && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.clientId) return setError("Elige un cliente.");
    if (!form.productId) return setError("Elige un producto del catálogo (o crea uno nuevo).");
    if (!form.jobName.trim()) return setError("Escribe el nombre del trabajo.");
    if (!cover?.file) return setError("El preview del trabajo es obligatorio (pega o sube una foto).");
    setSaving(true);
    try {
      const p = selectedProduct;
      const jobId = await createJob(db, {
        clientId: form.clientId,
        jobName: form.jobName,
        quantity: form.quantity,
        priority: form.priority,
        deliveryDate: form.deliveryDate,
        description: form.description,
        type: "Producto",
        createdVia: "lite",
        skuRef: p ? { skuId: p.id, version: p.version || 1 } : null,
        productSnapshot: p ? { sku: p.sku, name: p.name, categoryId: p.categoryId, attributes: p.attributes || {} } : null,
      }, user);

      // Sube el preview (obligatorio) y lo enlaza al job.
      const storageRef = ref(storage, `jobs/${jobId}/coverImage`);
      await uploadBytes(storageRef, cover.file);
      const coverImageUrl = await getDownloadURL(storageRef);
      await updateDoc(doc(db, "jobs", jobId), { coverImageUrl });

      navigate(`/jobs/${jobId}`);
    } catch (err) {
      setError(err.message || "No se pudo crear el trabajo.");
      setSaving(false);
    }
  };

  const labelCls = "block text-sm font-semibold text-gray-700 mb-1";
  const inputCls = "w-full p-3 text-base border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

  return (
    <div className="max-w-5xl mx-auto p-2">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Nuevo Trabajo · Producto</h1>
        <p className="text-sm text-gray-500">Elige del catálogo y crea el trabajo. Para booths/eventos usa Proyecto.</p>
      </div>

      {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna izquierda: campos */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            {/* Cliente */}
            <div className="md:col-span-2">
              <label className={labelCls}>Cliente *</label>
              <div className="flex gap-2">
                <select className={inputCls} value={form.clientId} onChange={(e) => set("clientId", e.target.value)} disabled={loading}>
                  <option value="">{loading ? "Cargando…" : "— Elige un cliente —"}</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name || c.companyName || "(sin nombre)"}</option>)}
                </select>
                <button type="button" onClick={() => setIsClientModalOpen(true)} className="shrink-0 px-4 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold">+ Nuevo</button>
              </div>
            </div>

            {/* Categoría */}
            <div>
              <label className={labelCls}>Categoría</label>
              <select className={inputCls} value={form.categoryId} onChange={(e) => { set("categoryId", e.target.value); set("productId", ""); }}>
                <option value="">— Todas —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {/* Producto (SKU) */}
            <div>
              <label className={labelCls}>Producto *</label>
              <div className="flex gap-2">
                <select className={inputCls} value={form.productId} onChange={(e) => set("productId", e.target.value)} disabled={loading}>
                  <option value="">{availableProducts.length ? "— Elige un producto —" : "— Sin productos (crea uno) —"}</option>
                  {availableProducts.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.sku}</option>)}
                </select>
                <button type="button" onClick={() => setIsProductModalOpen(true)} className="shrink-0 px-4 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold">+ Nuevo</button>
              </div>
              {selectedProduct && (
                <p className="text-xs text-gray-500 mt-1">
                  {Object.entries(selectedProduct.attributes || {}).filter(([k, v]) => v && !k.endsWith("Id")).map(([k, v]) => `${k}: ${v}`).join(" · ") || "Sin especificaciones"}
                </p>
              )}
            </div>

            {/* Nombre del trabajo */}
            <div className="md:col-span-2">
              <label className={labelCls}>Nombre del trabajo *</label>
              <input type="text" className={inputCls} value={form.jobName} onChange={(e) => set("jobName", e.target.value)} placeholder="Se autocompleta con el producto" />
            </div>

            {/* Cantidad */}
            <div>
              <label className={labelCls}>Cantidad</label>
              <input type="number" min="1" inputMode="numeric" className={inputCls} value={form.quantity} onChange={(e) => set("quantity", e.target.value)} />
            </div>
            {/* Prioridad */}
            <div>
              <label className={labelCls}>Prioridad</label>
              <select className={inputCls} value={form.priority} onChange={(e) => set("priority", e.target.value)}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            {/* Fecha de entrega */}
            <div>
              <label className={labelCls}>Fecha de entrega</label>
              <input type="date" className={inputCls} value={form.deliveryDate} onChange={(e) => set("deliveryDate", e.target.value)} />
            </div>
          </div>

          {/* Columna derecha: preview obligatorio */}
          <div className="lg:col-span-1">
            <label className={labelCls}>Preview del trabajo *</label>
            <PreviewUploader cover={cover} onSet={setCover} />
          </div>

          {/* Notas (ancho completo) */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-1">
              <label className={labelCls + " mb-0"}>Notas / detalles / contexto</label>
              <button
                type="button"
                onClick={handleImproveNotes}
                disabled={!form.description.trim() || improving}
                className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md bg-purple-100 text-purple-700 hover:bg-purple-200 disabled:opacity-40 disabled:cursor-not-allowed"
                title="La IA reescribe y enriquece el texto"
              >
                <Sparkles size={14} /> {improving ? "Mejorando…" : "Mejorar con IA"}
              </button>
            </div>
            <textarea rows={3} className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Detalles, instrucciones, contexto…" />
          </div>
        </div>

        {/* Acciones */}
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
          <button type="button" onClick={() => navigate("/dashboard")} className="px-5 py-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold">Cancelar</button>
          <button type="submit" disabled={!canSave} className="px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? "Creando…" : "Crear trabajo"}
          </button>
        </div>
      </form>

      <ClientModal isOpen={isClientModalOpen} onClose={() => setIsClientModalOpen(false)} onSave={handleSaveClient} />
      <ProductModal
        isOpen={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        onSave={handleSaveProduct}
        productData={null}
        categories={categories}
        clients={clients}
        materials={materials}
      />
    </div>
  );
};

export default NewJobLiteView;
